import React, { FunctionComponent, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Dialog,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemSecondaryAction,
  Box,
  CardActions,
  Button,
  Divider,
  useTheme,
} from '@mui/material';
// icons
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import CheckIcon from '@mui/icons-material/CheckCircle';

// styles
import { notificationStyles } from './Notification.styles';
// hooks
import { useNotification } from '@/hooks/useNotification';

// helpers
import { post } from '../../services/api';
import { useTranslation } from 'react-i18next';

interface NotificationProps {
  onClose(): void;
  anchorEl: HTMLElement | null;
}

const Notification: FunctionComponent<NotificationProps> = ({
  onClose,
  anchorEl,
}) => {
  // constants
  const { notifications, refetch } = useNotification();
  const { classes } = notificationStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    refetch();
  }, [location.pathname]);

  const cleanNotifications = () => {
    post('users/utils/clean_platform_notifications/', {}).then((response) => {
      if (response.status === 200) {
        refetch();
      }
    });
  };

  const redirect = (notification: any) => {
    if (notification.authorized_url) {
      window.location.href = notification.authorized_url;
    }
  };

  return (
    <Dialog
      fullWidth
      open={Boolean(anchorEl)}
      onClose={onClose}
      BackdropProps={{
        style: {
          background: 'transparent',
        },
      }}
      classes={{
        scrollPaper: classes.topScrollPaper,
        paperScrollBody: classes.topPaperScrollBody,
        paper: classes.paperDialog,
      }}
      maxWidth="xs"
    >
      <div
        className={'close-icon-navbar-notifications'}
        style={{
          position: 'absolute',
          width: '25px',
          height: '25px',
          left: '-10px',
          top: '-7px',
          backgroundColor: theme.palette.primary.main,
          zIndex: 2,
          borderRadius: '50%',
          display: 'flex',
          textAlign: 'center',
          justifyContent: 'center',
          alignItems: 'center',
          color: '#FFF',
          cursor: 'pointer',
        }}
        onClick={onClose}
      >
        <CloseIcon fontSize="small" />
      </div>
      <Card
        sx={{
          width: '100%',
          borderBottomRightRadius: '14px',
          borderBottomLeftRadius: '14px',
        }}
      >
        <CardHeader
          title={
            <Box component="h3" fontWeight="900" my={0} textAlign="center">
              {t('notifications.title')}
            </Box>
          }
          disableTypography
        />
        <CardContent className={classes.cardContent}>
          {notifications.length > 0 && (
            <List disablePadding>
              {notifications?.map((notification: any, index: number) => (
                <ListItem
                  alignItems="flex-start"
                  button
                  divider
                  key={index}
                  onClick={() => redirect(notification)}
                >
                  {notification.is_seen ? (
                    <CheckIcon className={classes.checkIcon} />
                  ) : (
                    <ErrorIcon className={classes.alertIcon} />
                  )}
                  <ListItemAvatar>
                    <Avatar />
                  </ListItemAvatar>
                  <Box>
                    <p className={classes.nameNotification}>
                      {notification.issuer_text}
                    </p>
                    <ListItemText
                      primary={notification.description}
                      secondary={notification.how_much_ago}
                      className={classes.descriptionText}
                    />{' '}
                  </Box>
                  <ListItemSecondaryAction>
                    <ArrowForwardIcon fontSize="small" color="primary" />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
          {notifications.length === 0 && (
            <Box px={2} mb={5}>
              <Box mb={1}>{t('notifications.zeroNotifications')}</Box>
              <Divider />
            </Box>
          )}
        </CardContent>
        <CardActions sx={{ justifyContent: 'center' }}>
          <Button
            variant="contained"
            sx={{
              borderRadius: '2em',
              backgroundColor: theme.palette.primary.main,
              color: '#FFF',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
              fontWeight: 'lighter',
              textTransform: 'none',
            }}
            className={'clean-notifications-navbar'}
            onClick={cleanNotifications}
          >
            {t('notifications.clearNotifications')}
          </Button>
        </CardActions>
      </Card>
    </Dialog>
  );
};

export default Notification;
